import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import type { ChatTurnIn, ProposedCall, ProposedChange, Project } from "../api/types";
import { ApiError } from "../api/client";
import { data } from "../data";
import { useAssistant, type Answer, type Hit } from "../lib/assistant";
import { isReady } from "../lib/flags";
import "./AssistantPanel.css";

/**
 * Figma: the dashboard right rail. MVP user story 3.
 *
 * Two things it does, both against the loaded projects rather than a model.
 *
 * It finds a project. Ask about a title, a person, a studio, a genre or a kind
 * of project and it answers with the ones that match, and opening one goes to
 * its page.
 *
 * It logs a call. Describe the call the way you would say it out loud and it
 * writes the call log entry and raises the update, instead of the manager
 * filling in four fields having just put the phone down. Paul asked for this
 * on the script and it is the part worth watching: the manager describes what
 * happened, the record follows.
 *
 * Nothing here is connected to an LLM, and the panel says so on screen. This
 * is deterministic matching over data already in the browser. When a model is
 * wired up it replaces the parsing in this file and nothing else.
 */

const clean = (s: string): string => s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").trim();

/**
 * Same production under two titles, collapsed to one.
 *
 * Two agencies send the same film under different titles, so a title search
 * otherwise answers with what looks like two projects. Paul's note on the
 * script is that resolving those is a backend concern and should not be
 * something the manager is shown, so the list is collapsed here for display.
 *
 * This is a stand-in, not the resolution logic. It compares normalised titles
 * and treats one containing the other as the same production, which is enough
 * for a title list and nothing like enough to merge records on. The real
 * decision is scored on several signals and lives in the API.
 */
function collapse(hits: Hit[]): Hit[] {
  const kept: Hit[] = [];
  for (const hit of hits) {
    const title = clean(hit.project.title);
    const same = kept.some((k) => {
      const other = clean(k.project.title);
      return title.startsWith(other) || other.startsWith(title);
    });
    if (!same) kept.push(hit);
  }
  return kept;
}

/** Words that mean the sentence is describing a call rather than asking. */
const CALL_VERBS = /\b(called|call(?:ed)? with|spoke|speaking|rang|talked|caught up|emailed|heard back|heard from|left a message|voicemail)\b/i;
const EMAIL_VERBS = /\b(emailed|email|wrote to|sent (?:over |through )?(?:an? )?email)\b/i;

/** "chemistry read next week" out of the back of a sentence. */
const FOLLOW_UP =
  /\b(?:follow(?:ing)? up (?:with|on|is|:)?|next step(?:s)? (?:is|are)?|then|and then|needs?|wants?|asking for|to send|send(?:ing)?)\s+(.{4,60}?)(?:[.;]|$)/i;

/**
 * What actually gets written in a follow up column.
 *
 * The generic pattern above wants a marker word like "next" or "needs".
 * "Chemistry read Tuesday" has none of them and is the single most common
 * thing a casting call ends with, so the trade's own vocabulary gets its own
 * pass rather than being missed.
 */
const CASTING_NEXT =
  /\b(chemistry read|call ?back|screen test|self ?tape|table read|audition|offer|avail(?:ability)? check|test)\b([^.;,]{0,32})/i;

function findFollowUp(text: string): string | null {
  const next = text.match(CASTING_NEXT);
  if (next) {
    const phrase = `${next[1]}${next[2] ?? ""}`.trim().replace(/[,;.]+$/, "");
    return phrase[0].toUpperCase() + phrase.slice(1);
  }
  const m = text.match(FOLLOW_UP);
  if (!m) return null;
  let found = m[1].trim().replace(/^to\s+/i, "").replace(/[,;.]+$/, "");
  // Cut at a word boundary rather than mid word. A follow up is a reminder,
  // so half a sentence of it is worse than a short one.
  if (found.length > 48) found = found.slice(0, 48).replace(/\s+\S*$/, "");
  if (found.length < 4) return null;
  return found[0].toUpperCase() + found.slice(1);
}

/**
 * Which project a sentence is about.
 *
 * A contact's name is checked before a title because that is how the sentence
 * is usually phrased. Nobody says "I called American Psycho Remake", they say
 * they called the casting director on it.
 */
/** Everybody the API says is attached to a production, however they arrived.
 *
 *  contacts is the shape the fixtures carry and the live payload does not
 *  return it at all, so a call naming a person could only ever be placed
 *  against a stub. key_people is what the API really sends, and it is the
 *  whole credited list, which is exactly who a manager rings.
 */
function peopleOn(project: Project): { name: string; role: string | null }[] {
  const named: { name: string; role: string | null }[] = (project.contacts ?? []).map((c) => ({
    name: c.name,
    role: c.role,
  }));
  const seen = new Set(named.map((c) => clean(c.name)));
  for (const name of project.key_people ?? []) {
    if (!seen.has(clean(name))) {
      seen.add(clean(name));
      named.push({ name, role: null });
    }
  }
  return named;
}

function locate(text: string, hits: Hit[]): { hit: Hit; contact: string | null } | null {
  const said = clean(text);
  const label = (who: { name: string; role: string | null }) =>
    who.role ? `${who.name} (${who.role})` : who.name;

  // Three passes rather than one, because they are not equally sure and the
  // first match wins. A full name is unambiguous. A production named outright
  // is unambiguous. A surname on its own is a guess, and across a few thousand
  // credited names it is a guess that would otherwise beat both of them purely
  // by being checked first, and file the call against the wrong production.
  for (const hit of hits) {
    for (const who of peopleOn(hit.project)) {
      if (said.includes(clean(who.name))) return { hit, contact: label(who) };
    }
  }
  for (const hit of hits) {
    const title = clean(hit.project.title);
    if (title.length > 3 && said.includes(title)) return { hit, contact: null };
  }
  for (const hit of hits) {
    for (const who of peopleOn(hit.project)) {
      const surname = clean(who.name).split(" ").slice(-1)[0];
      if (surname.length > 3 && said.includes(surname)) return { hit, contact: label(who) };
    }
  }
  return null;
}

/**
 * Trade shorthand against the words somebody actually types.
 *
 * The grids say Episodic and DevLit. Nobody asks for "episodic projects in
 * DevLit", they ask for TV in development, and a search that answers nothing
 * to that reads as broken rather than as precise.
 */
const SYNONYMS: Record<string, string[]> = {
  tv: ["episodic", "series", "pilots"],
  television: ["episodic", "series", "pilots"],
  series: ["episodic", "series", "pilots"],
  show: ["episodic", "series", "pilots"],
  shows: ["episodic", "series", "pilots"],
  film: ["feature film"],
  films: ["feature film"],
  movie: ["feature film"],
  movies: ["feature film"],
  feature: ["feature film"],
  features: ["feature film"],
  development: ["devlit"],
  dev: ["devlit"],
  casting: ["actively casting"],
  open: ["actively casting"],
};

/** A word, not a fragment of one. "Action" must not match "transaction". */
function mentions(said: string, phrase: string): boolean {
  const safe = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|\\s)${safe}(\\s|$)`).test(said);
}

/**
 * The plurals a manager actually types, and no more.
 *
 * "Any comedies" should find a Romantic Comedy. Stemming properly is a library
 * and a set of surprises; these three endings cover what gets typed at a panel
 * like this and anything else falls through to the exact word.
 */
function singulars(word: string): string[] {
  const forms = [word];
  if (word.endsWith("ies")) forms.push(`${word.slice(0, -3)}y`);
  else if (word.endsWith("es")) forms.push(word.slice(0, -2));
  if (word.endsWith("s")) forms.push(word.slice(0, -1));
  return forms;
}

function genresOf(project: Project): string[] {
  const source = project.genres?.length ? project.genres : project.genre ? [project.genre] : [];
  return source.flatMap((value) =>
    value
      .split(/[,/&]/)
      .map((part) => part.trim())
      .filter(Boolean),
  );
}

/**
 * The studios and genres these projects actually name.
 *
 * Read from the loaded projects rather than listed here, for the same reason
 * the directory's filters are: a fixed list goes stale the first time a grid
 * arrives with a studio nobody wrote down, and the panel then answers nothing
 * to a question about a real production.
 *
 * Genres arrive as one field holding several, "Action, Adventure, Fantasy", so
 * each is indexed whole and by its own words. Somebody asking for comedies
 * means the romantic ones too.
 */
function vocabulary(hits: Hit[]): { studios: Set<string>; genres: Set<string> } {
  const studios = new Set<string>();
  const genres = new Set<string>();

  for (const { project } of hits) {
    for (const name of [project.studio, project.network, project.production_company]) {
      const t = clean(name ?? "");
      if (t.length > 2) studios.add(t);
    }
    for (const part of genresOf(project)) {
      const whole = clean(part);
      if (whole.length > 2) genres.add(whole);
      for (const word of whole.split(" ")) {
        if (word.length > 3) genres.add(word);
      }
    }
  }
  return { studios, genres };
}

/**
 * A question that names a studio or a genre, answered on that alone.
 *
 * Worth separating from the scored search below. "What do we have at
 * Lionsgate" is not a request to rank things by how much they resemble the
 * word Lionsgate, it is a request for the Lionsgate ones, and scoring it
 * lets a production whose location happens to match outrank a real answer.
 *
 * It also lets the reply name what it did. "3 at Lionsgate" tells a manager
 * the panel understood the question; "3 projects match that" does not, and the
 * difference matters most when the answer is wrong.
 */
function byFacet(text: string, hits: Hit[]): { hits: Hit[]; text: string } | null {
  const said = clean(text);
  const words = said.split(" ").filter((w) => w.length > 2);
  const { studios, genres } = vocabulary(hits);

  const studio = [...studios].find((name) => mentions(said, name));
  if (studio) {
    const found = collapse(
      hits.filter(({ project }) =>
        [project.studio, project.network, project.production_company].some(
          (name) => clean(name ?? "") === studio,
        ),
      ),
    );
    if (found.length > 0) {
      const named =
        found[0].project.studio ?? found[0].project.network ?? found[0].project.production_company;
      return {
        // All of them. The sentence says "21 at Netflix" and the list used to
        // hold five, with nothing saying where the other sixteen went. The
        // reply draws the first few and offers the rest.
        hits: found,
        text: found.length === 1 ? `One at ${named}.` : `${found.length} at ${named}.`,
      };
    }
  }

  const asked = words.flatMap(singulars);
  const genre = [...genres].find(
    (name) => mentions(said, name) || (!name.includes(" ") && asked.includes(name)),
  );
  if (genre) {
    const found = collapse(
      hits.filter(({ project }) => {
        const all = clean(genresOf(project).join(", "));
        return all === genre || mentions(all, genre);
      }),
    );
    if (found.length > 0) {
      // Every word, not just the first. Normalising to compare lowercases the
      // whole thing, and "Romantic comedy" reads like a typo rather than a
      // genre somebody would recognise.
      const label = genre
        .split(" ")
        .map((w) => w[0].toUpperCase() + w.slice(1))
        .join(" ");
      return {
        hits: found,
        text: found.length === 1 ? `One ${label} project.` : `${found.length} ${label} projects.`,
      };
    }
  }

  return null;
}

/** Projects matching a plain question, best first. */
function search(text: string, hits: Hit[]): Hit[] {
  const said = clean(text);
  const words = said.split(" ").filter((w) => w.length > 2);
  if (words.length === 0) return [];

  const scored = hits
    .map((hit) => {
      const p = hit.project;
      const title = clean(p.title);
      const haystack = clean(
        [
          p.title,
          p.status_raw,
          p.project_type,
          p.studio,
          p.network,
          // Both were missing, so a question naming either found nothing at
          // all. Genre was not searchable anywhere in the panel.
          p.production_company,
          ...genresOf(p),
          p.location,
          ...p.key_people,
        ]
          .filter(Boolean)
          .join(" "),
      );
      let score = 0;
      if (said.includes(title) && title.length > 3) score += 10;
      for (const w of words) {
        if (title.includes(w)) score += 3;
        else if (haystack.includes(w)) score += 1;
        else if ((SYNONYMS[w] ?? []).some((alt) => haystack.includes(alt))) score += 1;
      }
      return { hit, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  return collapse(scored.map((s) => s.hit)).slice(0, 3);
}

/**
 * The exact role a named actor is up for on a production.
 *
 * Paul's idea: searching an actor should land on the part they are up for, not
 * the production, so he sees them without hunting through the roles. A match on
 * an actor otherwise opened the production at its first role, which is the one
 * place the actor might not be.
 *
 * Resolved against the real candidate lists rather than guessed, and only for
 * the one production the search landed on, so it is a couple of reads and not a
 * sweep. Falls back to null, and the caller to opening the production, whenever
 * the name is not an actor on a part here, which is the case for a director or
 * a producer whose name is on the production but not up for a role.
 */
async function roleForActor(hit: Hit, text: string): Promise<{ hit: Hit; text: string } | null> {
  const said = clean(text);
  const actor = (hit.project.key_people ?? []).find((name) => {
    const c = clean(name);
    return c.length > 3 && said.includes(c);
  });
  if (!actor) return null;

  try {
    const roles = await data.listProjectRoles(hit.project.id);
    for (const role of roles) {
      const candidates = await data.listRoleCandidates(role.id);
      if (candidates.some((c) => clean(c.name) === clean(actor))) {
        return {
          hit: { project: hit.project, roleId: role.id },
          text: `${actor} is up for ${role.name ?? "a role"} on ${hit.project.title}.`,
        };
      }
    }
  } catch {
    // A failed read is not worth an error here: the production still opens.
  }
  return null;
}

/** The sentence arriving the way a chat model writes, not all at once.
 *
 *  Only the newest answer does this. An older one has already been read, and
 *  retyping the whole thread on every question would be theatre.
 *
 *  One character at a time, with a longer beat at punctuation. Short lines
 *  are stretched so they still take about a second and a quarter, otherwise
 *  "Here it is." is gone before it reads as typing.
 */
function useTyped(text: string, on: boolean): { shown: string; done: boolean } {
  const [count, setCount] = useState(on ? 0 : text.length);

  useEffect(() => {
    if (!on || text.length === 0) {
      setCount(text.length);
      return;
    }
    setCount(0);
    const started = performance.now();
    let at = 0;
    let timer = 0;

    const step = () => {
      at += 1;
      setCount(at);
      if (at >= text.length) return;
      const just = text[at - 1];
      let pause = 40 + Math.random() * 15;
      if (just === "." || just === "!" || just === "?") pause = 180 + Math.random() * 40;
      else if (just === "," || just === ";" || just === ":") pause = 85 + Math.random() * 25;
      const left = text.length - at;
      const floor = Math.max(0, 1200 - (performance.now() - started)) / left;
      timer = window.setTimeout(step, Math.max(pause, floor));
    };

    timer = window.setTimeout(step, 48);
    return () => window.clearTimeout(timer);
  }, [text, on]);

  return { shown: text.slice(0, count), done: count >= text.length };
}


/**
 * A call the assistant would write, and the button that lets it.
 *
 * Shown rather than written, because the entry attaches to a production and
 * tells everyone following it, and the person who caused it cannot take it
 * back from here. 1v1 raised the same worry on the call. What it would say is
 * on screen in full before anybody agrees to it, so the mistake it guards
 * against, the right words on the wrong production, is one a reader can see.
 */
function Proposal({
  call,
  onConfirm,
  onOpenProject,
}: {
  call: ProposedCall;
  onConfirm: (call: ProposedCall) => Promise<void>;
  onOpenProject: (projectId: string) => void;
}) {
  const [state, setState] = useState<"asking" | "saving" | "done" | "written" | "failed">(
    "asking",
  );

  if (state === "written") {
    return (
      <div className="assistant__wrote">
        <span>Logged against {call.project_title}.</span>
        <button
          type="button"
          className="assistant__discard"
          onClick={() => onOpenProject(call.project_id)}
        >
          Open the call log
        </button>
      </div>
    );
  }
  if (state === "done") {
    // Turned down. Said rather than the card simply going, so it is clear
    // nothing was written rather than ambiguous.
    return <p className="assistant__wrote">Not logged.</p>;
  }

  return (
    <div className="assistant__proposal">
      <dl className="assistant__slip">
        <div>
          <dt>Project</dt>
          <dd>{call.project_title}</dd>
        </div>
        <div>
          <dt>With</dt>
          <dd>{call.contact}</dd>
        </div>
        <div>
          <dt>Notes</dt>
          <dd>{call.notes}</dd>
        </div>
        {call.follow_up && (
          <div>
            <dt>Follow up</dt>
            <dd>{call.follow_up}</dd>
          </div>
        )}
      </dl>
      {state === "failed" && (
        <p className="assistant__warn">That did not save. Nothing was written.</p>
      )}
      <div className="assistant__decide">
        <button
          type="button"
          className="assistant__confirm"
          disabled={state === "saving"}
          onClick={async () => {
            setState("saving");
            try {
              await onConfirm(call);
              setState("written");
            } catch {
              setState("failed");
            }
          }}
        >
          {state === "saving" ? "Logging" : "Log this call"}
        </button>
        <button
          type="button"
          className="assistant__discard"
          disabled={state === "saving"}
          onClick={() => setState("done")}
        >
          Not this one
        </button>
      </div>
    </div>
  );
}

/**
 * A change to the record the assistant would make, and the button that lets it.
 *
 * The grid-updates flow: a manager pastes an email about a production and the
 * assistant reads out a new production or an amendment to one we hold. Shown
 * rather than written, the same as a logged call, because a confirmed change
 * writes to a production and tells everyone following it, and the person who
 * caused it cannot take it back from here. 1v1 asked for the yes in the room.
 *
 * Everything the change would do is on screen first: each field as it is now
 * against what it would become, and any part it would add. A "review" is a
 * match the resolver was not sure of, so it names the production it thinks this
 * is and asks before it changes it, rather than changing the wrong one quietly.
 */
function ChangeProposal({
  change,
  onConfirm,
  onOpenProject,
}: {
  change: ProposedChange;
  onConfirm: (change: ProposedChange) => Promise<void>;
  onOpenProject: (projectId: string) => void;
}) {
  const [state, setState] = useState<"asking" | "saving" | "written" | "turned-down" | "failed">(
    "asking",
  );
  // Which production the manager picked, when a review offered more than one.
  // The default is the best match, which the resolver put first.
  const [picked, setPicked] = useState(0);

  const added = change.kind === "new";
  const confirmLabel = added ? "Add this production" : "Make these changes";

  // A review carries the near productions so the manager picks one rather than
  // taking the top guess. The pick drives what is shown and what gets written:
  // each candidate has its own title, id and diff, so choosing a different one
  // redraws the change with nothing fetched. Anything else is itself.
  const hasPicker = change.kind === "review" && change.alternatives.length > 1;
  const chosen = hasPicker ? change.alternatives[picked] : null;
  const activeTitle = chosen ? chosen.project_title : change.project_title;
  const activeFields = chosen ? chosen.fields : change.fields;
  const activeProjectId = chosen ? chosen.project_id : change.project_id;

  // What a yes writes: the picked production as a confirmed update, or, with no
  // picker, the change as proposed.
  const toWrite: ProposedChange = chosen
    ? {
        ...change,
        kind: "update",
        project_id: chosen.project_id,
        project_title: chosen.project_title,
        fields: chosen.fields,
        alternatives: [],
      }
    : change;

  if (state === "written") {
    return (
      <div className="assistant__wrote">
        <span>{added ? `Added ${activeTitle}.` : `Updated ${activeTitle}.`}</span>
        {activeProjectId && (
          <button
            type="button"
            className="assistant__discard"
            onClick={() => onOpenProject(activeProjectId as string)}
          >
            Open the production
          </button>
        )}
      </div>
    );
  }
  if (state === "turned-down") {
    return <p className="assistant__wrote">Nothing was changed.</p>;
  }

  return (
    <div className="assistant__change">
      <p
        className={`assistant__changekind assistant__changekind--${change.kind}`}
      >
        {change.kind === "new" && "New production"}
        {change.kind === "update" && `Update to ${change.project_title}`}
        {change.kind === "review" &&
          (hasPicker
            ? "Looks like one of these. Pick the right production."
            : `Looks like ${change.project_title}. Check before saving.`)}
      </p>

      {hasPicker && (
        <ul className="assistant__pick">
          {change.alternatives.map((c, i) => (
            <li key={c.project_id}>
              <label className="assistant__pickrow">
                <input
                  type="radio"
                  name="which-production"
                  checked={i === picked}
                  onChange={() => setPicked(i)}
                />
                <span className="assistant__picktitle">{c.project_title}</span>
                <span className="assistant__pickhint">
                  {c.fields.length === 0
                    ? "no change"
                    : c.fields.length === 1
                      ? "1 change"
                      : `${c.fields.length} changes`}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {activeFields.length > 0 && (
        <dl className="assistant__diff">
          {activeFields.map((f) => (
            <div key={f.field}>
              <dt>{f.field}</dt>
              <dd>
                {!added && (
                  <span className="assistant__was">{f.before ?? "not set"}</span>
                )}
                <span className="assistant__now">{f.after}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {change.roles.length > 0 && (
        <div className="assistant__addroles">
          <span className="assistant__addroleshead">
            {change.roles.length === 1 ? "Role to add" : "Roles to add"}
          </span>
          <ul>
            {change.roles.map((r) => (
              <li key={r.name}>
                <span className="assistant__rolename">{r.name}</span>
                {r.standing && <span className="assistant__rolestanding">{r.standing}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeFields.length === 0 && change.roles.length === 0 && (
        <p className="assistant__warn">
          I could not read any details to change from that. Add a line like "Studio:
          Lionsgate" or "Cast: Zendaya (offer)" and try again.
        </p>
      )}

      {state === "failed" && (
        <p className="assistant__warn">That did not save. Nothing was changed.</p>
      )}

      <div className="assistant__decide">
        <button
          type="button"
          className="assistant__confirm"
          disabled={
            state === "saving" || (activeFields.length === 0 && change.roles.length === 0)
          }
          onClick={async () => {
            setState("saving");
            try {
              await onConfirm(toWrite);
              setState("written");
            } catch {
              setState("failed");
            }
          }}
        >
          {state === "saving" ? "Saving" : confirmLabel}
        </button>
        <button
          type="button"
          className="assistant__discard"
          disabled={state === "saving"}
          onClick={() => setState("turned-down")}
        >
          {change.kind === "review" ? "Not this one" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

/**
 * Whether a message is a production to add or amend rather than a question.
 *
 * Kept deliberate so it never hijacks an ordinary question. Either it opens
 * with "add/new/update project", or it is a pasted block that labels a title,
 * which is how a forwarded email or a few lines off a grid actually read.
 */
const PROJECT_COMMAND = /^(add|new|update|create)\s+(a\s+)?(project|production)\b/i;

function looksLikeProjectPaste(text: string): boolean {
  if (PROJECT_COMMAND.test(text.trim())) return true;
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.length >= 2 && lines.some((l) => /^(title|project|production)\s*:/i.test(l));
}

/** The sentence over a proposed change, which depends on how sure it is. */
function changeIntro(change: ProposedChange): string {
  if (change.kind === "new") {
    return "This looks like a production we do not have yet. Here is what I would add. Nothing is saved until you say so.";
  }
  if (change.kind === "review") {
    return `I think this is about ${change.project_title}. Check it is the right one, then I will make these changes.`;
  }
  return `Here is what I would change on ${change.project_title}. Nothing is saved until you say so.`;
}

/** How many productions an answer lists before it offers the rest. Enough to
 *  see that the answer is the right shape, short enough that the sentence
 *  above it is still on screen. */
const SHOWN = 5;

/** One answer: the sentence, and whatever the sentence is about. */
/**
 * Answers that have already been typed out once, by their text.
 *
 * Paul, 7 October: open and close the panel and the last answer typed itself
 * out again, as if it had just arrived. The conversation lives above the
 * panel and survives, but the panel does not: closing it, opening it, or
 * moving between the dashboard's rail and the dock mounts a fresh one, and a
 * fresh Reply on the newest answer started typing from the first letter.
 *
 * Held here, outside any one panel, so every panel agrees. Keyed by the text
 * rather than the object, because confirming a proposed call replaces the
 * answer object and that is not a new answer.
 */
const TYPED = new Set<string>();

function Reply({
  answer,
  onOpen,
  onOpenProject,
  onConfirm,
  onConfirmChange,
  typing,
  onGrow,
}: {
  answer: Answer;
  onOpen: (hit: Hit) => void;
  /** A citation names a production, not a role, and the project page is routed
   *  on a role. Resolved by the panel, which holds both. */
  onOpenProject: (projectId: string) => void;
  onConfirm: (call: ProposedCall) => Promise<void>;
  onConfirmChange: (change: ProposedChange) => Promise<void>;
  typing: boolean;
  onGrow?: (smooth?: boolean) => void;
}) {
  // Decided once, when this Reply mounts. An answer types the first time it
  // is shown and is simply there every time after.
  const [animate] = useState(() => typing && !TYPED.has(answer.text));
  useEffect(() => {
    TYPED.add(answer.text);
  }, [answer.text]);
  const { shown, done } = useTyped(answer.text, animate);
  // Expanded per answer rather than per panel, so opening one long list does
  // not open every other list in the thread.
  const [all, setAll] = useState(false);
  useEffect(() => {
    onGrow?.(done);
  }, [shown, done, onGrow]);
  // Expanding grows the panel, and the thread follows its own bottom, so the
  // list has to say it changed height the same way the typing does.
  useEffect(() => {
    onGrow?.(true);
  }, [all, onGrow]);
  return (
    <>
      <p className="assistant__reply">
        {shown}
        {animate && !done && <span className="assistant__caret" aria-hidden="true" />}
      </p>
      {/* What the sentence is about waits for the sentence to finish, so the
          answer is not read backwards from a list that appeared first. */}
      {done && (
      <>

            {answer.kind === "projects" && (
              <ul className="assistant__matches">
                {answer.hits.slice(0, all ? answer.hits.length : SHOWN).map((hit) => (
                  <li key={hit.project.id}>
                    <button
                      type="button"
                      className="assistant__match"
                      onClick={() => onOpen(hit)}
                    >
                      <span className="assistant__portrait" aria-hidden="true" />
                      <span className="assistant__matchbody">
                        <span className="assistant__matchtitle">{hit.project.title}</span>
                        <span className="assistant__matchmeta">
                          {[hit.project.status_raw, hit.project.studio ?? hit.project.network]
                            .filter(Boolean)
                            .join(" • ")}
                        </span>
                      </span>
                      <span className="assistant__pick">Open</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* 1v1 asked a studio question, was told "21 at Netflix", and got
                five cards. The count is the honest answer and the list was
                not, so the rest are one click away rather than gone. */}
            {answer.kind === "projects" && !all && answer.hits.length > SHOWN && (
              <button
                type="button"
                className="assistant__viewall"
                onClick={() => setAll(true)}
              >
                View all {answer.hits.length}
              </button>
            )}

            {answer.kind === "model" && answer.citations.length > 0 && (
              // The rows the answer was built from. This is the only way a
              // reader can check it: nothing on this side decided what the
              // sentence above says, and the data underneath comes out of
              // agency grids people already go back to the PDF to verify.
              <ul className="assistant__matches">
                {answer.citations.map((cite) => (
                  <li key={cite.id}>
                    <button
                      type="button"
                      className="assistant__match"
                      onClick={() => onOpenProject(cite.id)}
                    >
                      <span className="assistant__portrait" aria-hidden="true" />
                      <span className="assistant__matchbody">
                        <span className="assistant__matchtitle">{cite.label}</span>
                        {cite.detail && (
                          <span className="assistant__matchmeta">{cite.detail}</span>
                        )}
                      </span>
                      <span className="assistant__pick">Open</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {((answer.kind === "model" && answer.proposed) || answer.kind === "offer") && (
              <Proposal
                call={answer.kind === "offer" ? answer.proposed : answer.proposed!}
                onConfirm={onConfirm}
                onOpenProject={onOpenProject}
              />
            )}

            {answer.kind === "change" && (
              <ChangeProposal
                change={answer.change}
                onConfirm={onConfirmChange}
                onOpenProject={onOpenProject}
              />
            )}

      </>
      )}
    </>
  );
}

export default function AssistantPanel({
  /** Whether somebody can currently see this.
   *
   *  The dashboard rail always can, so it defaults to true. The dock keeps the
   *  panel mounted while closed so that opening it can animate, and a panel
   *  nobody has opened must not be loading anything. */
  active = true,
}: {
  active?: boolean;
} = {}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // The productions and the conversation both live above this component, so
  // the dashboard rail and the dock are two views of one assistant rather
  // than two assistants. An answer of null is a question still in the air.
  const { hits, loading, failed, ensure, turns, setTurns, connected } = useAssistant();
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const growRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const thinkTimer = useRef<number>(0);
  const thinkResolve = useRef<((keep: boolean) => void) | null>(null);
  const askGen = useRef(0);

  // Read once, the first time somebody is actually looking at this.
  //
  // Not on mount. The dock stays mounted on every screen so that opening it
  // can animate, and loading on mount meant every navigation read every role
  // with its production for a panel nobody had opened.
  useEffect(() => {
    if (active) ensure();
  }, [active, ensure]);

  // Any loaded production will do for an example. This used to insist on one
  // carrying contacts, which the live payload does not return, so on real
  // data every example fell back to describing itself: "Show me a project by
  // name" instead of naming one, and a call naming nobody in particular.
  const example = hits[0];
  // Somebody actually credited on it, so the call suggestion is one the panel
  // can place. A full name rather than a surname, because a surname alone is
  // the weakest of the three ways a call is matched.
  const exampleContact = hits.find((h) => (h.project.key_people ?? []).length > 0)
    ?.project.key_people?.[0];

  // Always something to click. These were built only from a production that
  // happened to carry a contact, so before anything had loaded, or on a set
  // where no production has one, the panel offered a blank space above the
  // box and no hint of what it can be asked.
  //
  // Each one is a shape the panel really answers: a title, a studio, a
  // genre, and a call to write up. Filled in from live values when there are
  // any, so the examples name productions the manager recognises.
  const exampleStudio = hits.find((h) => h.project.studio)?.project.studio;
  const exampleGenre = hits.flatMap((h) => genresOf(h.project))[0];
  const suggestions = [
    example ? `Show me ${example.project.title}` : "Show me a project by name",
    exampleStudio ? `What is ${exampleStudio} doing` : "What is a studio doing",
    exampleGenre
      ? `Find the ${exampleGenre.toLowerCase()} projects`
      : "Find TV projects in development",
    exampleContact
      ? `Spoke to ${exampleContact}, keen, chemistry read next week`
      : "Spoke to the casting director, keen, chemistry read next week",
  ];

  /** Why the model last failed, if it has, so the panel can say the answers
   *  below it came from the productions it has loaded rather than from the
   *  model, and say what went wrong rather than only that something did. */
  const [fell, setFell] = useState<string | null>(null);

  /** Folded away by somebody who does not need the examples any more. Held
   *  for the session rather than forever: it is a reminder of what the panel
   *  can be asked, and the answer to "do I still need this" changes. */
  const [folded, setFolded] = useState(() => {
    try {
      return sessionStorage.getItem("castflow.assistant.examples") === "off";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem("castflow.assistant.examples", folded ? "off" : "on");
    } catch {
      // A browser refusing storage is not a reason to stop drawing the panel.
    }
  }, [folded]);

  // Grow the composer to fit what is in it, up to the ceiling the CSS sets.
  // A pasted email is several lines, and a one line box that hid all but the
  // last of them was the thing that made a paste read as one long line.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  /** Attach an answer to the question still waiting at the end of the thread. */
  function reply(given: Answer) {
    setTurns((current) =>
      current.map((turn, i) => (i === current.length - 1 ? { ...turn, answer: given } : turn)),
    );
  }

  useEffect(() => {
    return () => {
      window.clearTimeout(thinkTimer.current);
      thinkResolve.current?.(false);
    };
  }, []);

  function waitThink(): Promise<boolean> {
    thinkResolve.current?.(false);
    window.clearTimeout(thinkTimer.current);
    return new Promise((resolve) => {
      thinkResolve.current = resolve;
      thinkTimer.current = window.setTimeout(() => {
        thinkResolve.current = null;
        resolve(true);
      }, 750);
    });
  }

  async function holdThenReply(given: Answer, gen: number) {
    const keep = await waitThink();
    if (!keep || gen !== askGen.current) return;
    reply(given);
  }

  const follow = useCallback((smooth = false) => {
    const body = bodyRef.current;
    if (!body || !stickToBottom.current) return;
    body.dataset.pinning = "1";
    body.scrollTo({
      top: body.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
    const clear = () => {
      delete body.dataset.pinning;
    };
    if (smooth) {
      body.addEventListener("scrollend", clear, { once: true });
      window.setTimeout(clear, 450);
    } else {
      requestAnimationFrame(clear);
    }
  }, []);

  // Follow the newest reply as it types and as the cards land. The old
  // observer only pinned when the jump was under 80px, so a wrap or the
  // project list left the rest of the answer below the fold.
  //
  // Somebody who has scrolled up to read an earlier answer is left there.
  // Coming back to the bottom turns following on again.
  useEffect(() => {
    const body = bodyRef.current;
    const grown = growRef.current;
    if (!body || !grown) return;

    const onScroll = () => {
      if (body.dataset.pinning) return;
      const gap = body.scrollHeight - body.clientHeight - body.scrollTop;
      stickToBottom.current = gap < 48;
    };
    body.addEventListener("scroll", onScroll, { passive: true });

    const observer = new ResizeObserver(() => follow(false));
    observer.observe(grown);
    follow(false);

    return () => {
      body.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [follow, turns]);

  async function ask(text: string) {
    window.clearTimeout(thinkTimer.current);
    thinkResolve.current?.(false);
    const gen = ++askGen.current;
    stickToBottom.current = true;
    setTurns((current) => [...current, { question: text, answer: null }]);
    setDraft("");
    setBusy(true);
    try {
      /**
       * The model answers when there is one, and the matcher below answers
       * when there is not.
       *
       * Kept rather than replaced. The matcher is what 1v1 has already been
       * shown and it works today, so deleting it would trade a working panel
       * for one waiting on a key. This way the panel improves itself the
       * moment the model is configured, with no deploy, and if the endpoint
       * falls over it falls back to something that answers rather than to an
       * apology.
       */
      if (connected) {
        const thread: ChatTurnIn[] = [
          ...turns.flatMap((t) =>
            t.answer
              ? [
                  { role: "user" as const, content: t.question },
                  { role: "assistant" as const, content: t.answer.text },
                ]
              : [],
          ),
          { role: "user" as const, content: text },
        ];
        try {
          // Writes are confirmed from this screen. The assistant can log a
          // call that notifies everyone following a production, and a model
          // deciding that on its own is what 1v1 worried about out loud.
          const answer = await data.askAssistant(thread, true);
          await holdThenReply(
            {
              kind: "model",
              text: answer.reply || "I could not find an answer to that.",
              citations: answer.citations,
              proposed: answer.proposed_call,
            },
            gen,
          );
          return;
        } catch (err) {
          // Said rather than swallowed, and then the matcher still answers, so
          // the reader gets something either way.
          //
          // The reason is kept and shown. Without it the panel degraded
          // silently: a green badge, an answer that looked like any other, and
          // nothing anywhere saying the model had not been involved or why.
          // A key on the wrong host and a model that is simply busy produce
          // the same sentence, and they are not the same problem.
          setFell(err instanceof ApiError ? `${err.status}: ${err.detail}` : "No reply.");
        }
      }

      // A pasted production to add or amend is checked before anything else,
      // because it is a statement with a shape of its own, and reading it as a
      // search or a call would answer the wrong thing. It is deliberately hard
      // to trigger by accident: a command, or a labelled block.
      if (looksLikeProjectPaste(text)) {
        const change = await data.proposeProjectChange(text);
        await holdThenReply({ kind: "change", text: changeIntro(change), change }, gen);
        return;
      }

      // Describing a call is checked first. "Spoke to Jessica about the
      // Bateman role" is a statement, and treating it as a search would
      // answer a question nobody asked.
      if (CALL_VERBS.test(text)) {
        const found = locate(text, hits);
        if (found) {
          const how = EMAIL_VERBS.test(text) ? "email" : "phone";
          // Offered rather than written, the same as the model path. This used
          // to write the row here, so saying "spoke to Jessica" put a real
          // entry against a production and told everyone following it before
          // anybody had seen which production it picked.
          await holdThenReply(
            {
              kind: "offer",
              text: "Here is what I would log. Check the production before you say yes.",
              proposed: {
                project_id: found.hit.project.id,
                project_title: found.hit.project.title,
                contact: found.contact ?? "Not recorded",
                channel: how,
                notes: text.trim(),
                follow_up: findFollowUp(text),
              },
            },
            gen,
          );
          return;
        }
        await holdThenReply(
          {
            kind: "none",
            text: "I could not tell which project that call was about. Name the person or the project and I will log it.",
          },
          gen,
        );
        return;
      }

      // A question that names a studio or a genre is answered on that, and
      // says so. Everything else falls through to the scored search.
      const facet = byFacet(text, hits);
      if (facet) {
        await holdThenReply({ kind: "projects", text: facet.text, hits: facet.hits }, gen);
        return;
      }

      const results = search(text, hits);

      // If the question named an actor on the top result, land on the role they
      // are up for rather than the production. Only the top one is resolved, so
      // this is a couple of reads, and it falls through to the plain answer when
      // the name is not an actor up for a part here.
      if (results.length > 0) {
        const landed = await roleForActor(results[0], text);
        if (landed) {
          await holdThenReply(
            {
              kind: "projects",
              text: landed.text,
              hits: [landed.hit, ...results.slice(1)],
            },
            gen,
          );
          return;
        }
      }

      await holdThenReply(
        results.length > 0
          ? {
              kind: "projects",
              text:
                results.length === 1
                  ? "Here it is."
                  : `${results.length} projects match that.`,
              hits: results,
            }
          : { kind: "none", text: "Nothing in the grids matches that." },
        gen,
      );
    } finally {
      if (gen === askGen.current) setBusy(false);
    }
  }

  function open(hit: Hit) {
    if (isReady("project")) navigate(`/roles/${hit.roleId}`);
  }

  /**
   * Open a production the model cited.
   *
   * A citation names a production and the project page is routed on a role, so
   * the id has to be turned into one of its roles. The productions this panel
   * has loaded carry both, which covers everything with a role on it. The
   * handful that have none are not openable and the card simply does not go
   * anywhere, which is better than routing to a page that cannot draw.
   */
  function openProject(projectId: string) {
    const hit = hits.find((h) => h.project.id === projectId);
    if (hit) open(hit);
  }

  /** Write the call the assistant offered, through the same endpoint the
   *  record call screen uses. Nothing new writes; the assistant proposes and
   *  this is the ordinary path. */
  async function confirmCall(call: ProposedCall) {
    await data.addCall({
      project_id: call.project_id,
      occurred_at: new Date().toISOString(),
      contact: call.contact,
      kind: call.channel === "email" ? "Email" : "Phone",
      notes: call.notes,
      follow_up: call.follow_up,
    });
  }

  /** Write the change the assistant proposed. The same shape as confirming a
   *  call: the assistant describes, this is the yes, and the write announces
   *  itself so the feed and the bell pick it up. */
  async function confirmChange(change: ProposedChange) {
    await data.applyProjectChange(change);
  }

  return (
    <section className="assistant">
      <header className="assistant__head">
        {/* Title and the status pill share the top row; the subtitle and the
            log link run full width below, so the subtitle is not squeezed by
            the pill into wrapping its last word onto its own line. */}
        <div className="assistant__headrow">
          <h4 className="assistant__title">
            <span className="assistant__spark" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.6l1.5 3.9 3.9 1.5-3.9 1.5L8 12.4 6.5 8.5 2.6 7l3.9-1.5L8 1.6Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            AI Assistant
          </h4>
          {/* Asked rather than written into the markup. It said "Not connected"
              in the source, so it would have kept saying that on the day the
              model was wired up and the first person to notice would have been
              in a demo. Nothing while it does not know yet: a badge that guesses
              is the thing being fixed. */}
          {connected !== null && (
            <span
              className={`assistant__status ${connected ? "assistant__status--on" : ""}`}
            >
              {connected ? "Connected" : "Not connected"}
            </span>
          )}
        </div>
        <p className="assistant__sub">Ask about a project, or tell me about a call.</p>
        {/* Where to check an answer against what was actually looked up.
            Here, because this is where the test questions are asked. */}
        {isReady("assistant-log") && (
          <Link className="assistant__loglink" to="/assistant/log">
            See what it looked up
          </Link>
        )}
      </header>

      <div className="assistant__body" ref={bodyRef}>
        <div className="assistant__turns" ref={growRef}>
        {/* Said out loud, because the alternative is a panel that answers
            "nothing matches" to everything and looks like an honest miss. It
            used to mark itself loaded before the request, so one failed page
            left it holding nothing and certain, until a hard reload. */}
        {fell && (
          // Said, because an answer from the productions in the browser and an
          // answer from the model read the same and are not the same thing.
          <p className="assistant__warn">
            The model did not answer, so this is coming from the productions
            loaded here instead. <span className="assistant__why">{fell}</span>
          </p>
        )}
        {failed && (
          <p className="assistant__warn">
            I could not read the productions, so there is nothing here to answer
            from.{" "}
            <button type="button" className="assistant__retry" onClick={ensure}>
              Try again
            </button>
          </p>
        )}
        {turns.length === 0 ? (
          /* Empty to start with, which is what 1v1 asked for. There were two
             paragraphs saying the same thing, one under the title and one
             here, above a panel with nothing in it. The subtitle says what
             this is and the questions underneath say what it can be asked,
             so a third sentence was only taking the room the answers need.
             The loading line stays: a panel that is still reading should say
             so rather than look empty. */
          loading ? <p className="assistant__empty">Reading the productions.</p> : null
        ) : (
          turns.map((turn, index) => (
            <div className="assistant__thread" key={index}>
              <p className="assistant__bubble">{turn.question}</p>
              {turn.answer === null ? (
                <p className="assistant__reply assistant__reply--wait" aria-label="Thinking">
                  <span className="assistant__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </p>
              ) : (
                <Reply
                  answer={turn.answer}
                  onOpen={open}
                  onOpenProject={openProject}
                  onConfirm={confirmCall}
                  onConfirmChange={confirmChange}
                  typing={index === turns.length - 1}
                  onGrow={follow}
                />
              )}
            </div>
          ))
        )}
        </div>
      </div>

      {/* First time only. They are there to show what this can be asked, and
          once somebody has asked something they have served that purpose and
          are taking room from the conversation.

          Foldable even then. 1v1 asked for a way to put them away, in their
          words a chevron or an X, because the block takes a lot of room and
          somebody who already knows what to ask does not need it. Remembered
          for the session, so folding it once does not have to be done again
          on the next screen. */}
      {turns.length === 0 && (
      <div className="assistant__suggestions">
        <div className="assistant__suggesthead">
          <span>Try asking</span>
          <button
            type="button"
            className="assistant__fold"
            aria-expanded={!folded}
            onClick={() => setFolded((on) => !on)}
            title={folded ? "Show the example questions" : "Hide the example questions"}
          >
            <span className="assistant__foldlabel">{folded ? "Show" : "Hide"}</span>
            <svg
              className={folded ? "" : "assistant__foldicon--open"}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5 6 7.5 9 4.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        {!folded && suggestions.map((s) => (
          <button
            key={s}
            className="assistant__chip"
            onClick={() => {
              // Into the box, not straight out. Every one of these wants a
              // name changed or a detail added before it is worth asking,
              // and sending it as written answers a question nobody had.
              setDraft(s);
              const box = inputRef.current;
              if (box) {
                box.focus();
                // Caret at the end, so typing continues the sentence rather
                // than landing in front of it.
                requestAnimationFrame(() => box.setSelectionRange(s.length, s.length));
              }
            }}
            type="button"
            disabled={busy}
          >
            {s}
          </button>
        ))}
      </div>
      )}

      <form
        className="assistant__composer"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) void ask(draft.trim());
        }}
      >
        <textarea
          ref={inputRef}
          value={draft}
          rows={1}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter is a new line, so a pasted email keeps
            // its shape while a one line question still goes on Enter.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (draft.trim() && !busy) void ask(draft.trim());
            }
          }}
          placeholder="Ask anything, paste a production, or say what happened on a call..."
          aria-label="Ask the assistant"
        />
        <button type="submit" aria-label="Send" disabled={!draft.trim() || busy}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M14.2 1.8 7.3 8.7M14.2 1.8l-4.4 12.4-2.5-5.5-5.5-2.5 12.4-4.4Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </section>
  );
}
